<template>
  <div class="fmap">
    <div ref="map" class="fmap-canvas"></div>

    <div class="fmap-tools">
      <button type="button" :class="{ on: mode === 'map' }" @click="setMode('map')"><i class="fas fa-map"></i> Map</button>
      <button type="button" :class="{ on: mode === 'satellite' }" @click="setMode('satellite')"><i class="fas fa-satellite"></i> Satellite</button>
    </div>

    <div class="fmap-legend" v-if="points.length">
      <div class="fl-title">{{ fmt(points.length) }} forests</div>
      <div class="fl-scale"><span>low</span><i></i><span>high CO2 / ha</span></div>
      <div class="fl-pulse"><span class="fl-dot"></span> scanned in the last 2 years</div>
    </div>

    <div v-if="!points.length" class="fmap-loading">
      <i class="fas fa-globe-europe fa-spin"></i>
      <span>{{ stats.insights ? 'No forests with a location yet' : 'Loading the forests…' }}</span>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import { MAPBOX_TOKEN } from '../../../utils/mapbox';
import { fmt, imageUrl, isDark, themeState } from '../theme';

const STYLES = {
  dark: 'mapbox://styles/mapbox/dark-v11',
  light: 'mapbox://styles/mapbox/light-v11',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
};
const esc = (s) => String(s === null || s === undefined ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export default {
  name: 'ForestMap',
  props: { stats: { type: Object, required: true } },
  data() {
    return { mode: 'map', map: null, ready: false, flown: false, pulseFrame: null, resizeObserver: null };
  },
  computed: {
    points() {
      return (this.stats.insights && this.stats.insights.map) || [];
    },
    geojson() {
      const year = new Date().getFullYear();
      return {
        type: 'FeatureCollection',
        features: this.points.map((f) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [f.lng, f.lat] },
          properties: {
            id: f.id, name: f.name, country: f.country, ha: f.ha, trees: f.trees, co2: f.co2, available: f.available,
            density: f.ha > 0 ? f.co2 / f.ha : 0, recent: f.scanYear && f.scanYear >= year - 1 ? 1 : 0, image: f.image || '',
          },
        })),
      };
    },
    styleUrl() {
      void themeState.tick;
      return this.mode === 'satellite' ? STYLES.satellite : (isDark() ? STYLES.dark : STYLES.light);
    }
  },
  watch: {
    geojson() { this.updateData(); },
    styleUrl(url) { if (this.map) this.map.setStyle(url); }
  },
  mounted() {
    mapboxgl.accessToken = MAPBOX_TOKEN;
    this.map = new mapboxgl.Map({
      container: this.$refs.map,
      style: this.styleUrl,
      projection: 'globe',
      center: [12, 38],
      zoom: 0.9,
      attributionControl: false,
      cooperativeGestures: false,
    });
    this.map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
    this.map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
    this.map.on('style.load', this.onStyle);
    this.map.on('click', 'clusters', this.zoomCluster);
    this.map.on('click', 'points', this.showPopup);
    for (const layer of ['clusters', 'points']) {
      this.map.on('mouseenter', layer, () => { this.map.getCanvas().style.cursor = 'pointer'; });
      this.map.on('mouseleave', layer, () => { this.map.getCanvas().style.cursor = ''; });
    }
    // the popup's link opens the forest inside the app
    this.$el.addEventListener('click', this.onLinkClick);
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.map && this.map.resize());
      this.resizeObserver.observe(this.$el);
    }
  },
  beforeDestroy() {
    this.$el.removeEventListener('click', this.onLinkClick);
    if (this.pulseFrame) cancelAnimationFrame(this.pulseFrame);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.map) this.map.remove();
  },
  methods: {
    fmt,
    setMode(m) { this.mode = m; },
    onLinkClick(e) {
      const a = e.target.closest && e.target.closest('.fp-link');
      if (!a) return;
      e.preventDefault();
      this.$router.push(`/areas/${a.dataset.area}`);
    },
    onStyle() {
      const map = this.map;
      const dark = this.mode === 'satellite' || isDark();
      map.setFog({
        color: dark ? 'rgb(12, 24, 18)' : 'rgb(232, 242, 236)',
        'high-color': dark ? 'rgb(20, 70, 60)' : 'rgb(150, 200, 230)',
        'horizon-blend': 0.06,
        'space-color': dark ? 'rgb(4, 8, 10)' : 'rgb(206, 226, 240)',
        'star-intensity': dark ? 0.35 : 0,
      });
      map.addSource('forests', { type: 'geojson', data: this.geojson, cluster: true, clusterMaxZoom: 8, clusterRadius: 42, clusterProperties: { co2: ['+', ['get', 'co2']] } });
      map.addSource('forests-raw', { type: 'geojson', data: this.geojson });

      map.addLayer({
        id: 'heat', type: 'heatmap', source: 'forests-raw', maxzoom: 7,
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'co2'], 0, 0.1, 500, 1],
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 0.8, 7, 2],
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 6, 7, 26],
          'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 3, 0.85, 7, 0],
          'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'],
            0, 'rgba(0,0,0,0)', 0.2, 'rgba(61,177,110,0.35)', 0.45, 'rgba(61,220,151,0.6)', 0.7, 'rgba(47,208,196,0.85)', 1, 'rgba(201,247,179,0.95)'],
        },
      });
      map.addLayer({
        id: 'clusters', type: 'circle', source: 'forests', filter: ['has', 'point_count'], minzoom: 3,
        paint: {
          'circle-color': 'rgba(61, 177, 110, 0.82)',
          'circle-radius': ['step', ['get', 'point_count'], 14, 10, 19, 50, 25, 200, 32],
          'circle-stroke-width': 3,
          'circle-stroke-color': 'rgba(201, 247, 179, 0.55)',
        },
      });
      map.addLayer({
        id: 'cluster-count', type: 'symbol', source: 'forests', filter: ['has', 'point_count'], minzoom: 3,
        layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-size': 12, 'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'] },
        paint: { 'text-color': '#ffffff' },
      });
      map.addLayer({
        id: 'pulse', type: 'circle', source: 'forests', filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'recent'], 1]], minzoom: 5,
        paint: { 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-color': '#3ddc97', 'circle-stroke-width': 2, 'circle-radius': 10, 'circle-stroke-opacity': 0.8 },
      });
      map.addLayer({
        id: 'points', type: 'circle', source: 'forests', filter: ['!', ['has', 'point_count']], minzoom: 3,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['sqrt', ['get', 'co2']], 0, 4, 10, 7, 30, 13, 60, 20],
          'circle-color': ['interpolate', ['linear'], ['get', 'density'], 0, '#9ed77f', 40, '#3ddc97', 90, '#2fd0c4', 160, '#f4c25b'],
          'circle-opacity': 0.88,
          'circle-stroke-width': 1.5,
          'circle-stroke-color': 'rgba(255,255,255,0.85)',
        },
      });
      this.ready = true;
      this.startPulse();
      if (!this.flown && this.points.length) this.flyToForests();
    },
    updateData() {
      if (!this.ready) return;
      const a = this.map.getSource('forests');
      const b = this.map.getSource('forests-raw');
      if (a) a.setData(this.geojson);
      if (b) b.setData(this.geojson);
      if (!this.flown && this.points.length) this.flyToForests();
    },
    flyToForests() {
      this.flown = true;
      const bounds = new mapboxgl.LngLatBounds();
      for (const f of this.points) bounds.extend([f.lng, f.lat]);
      // a slow spin of the globe, then the flight to the forests
      this.map.easeTo({ center: [bounds.getCenter().lng - 40, 30], zoom: 1.2, duration: 1600, easing: (t) => t });
      setTimeout(() => {
        if (!this.map) return;
        this.map.fitBounds(bounds, { padding: { top: 60, bottom: 60, left: 60, right: 60 }, maxZoom: 7.5, duration: 4200, curve: 1.6, pitch: 35, essential: true });
      }, 1700);
    },
    startPulse() {
      if (this.pulseFrame) cancelAnimationFrame(this.pulseFrame);
      const t0 = performance.now();
      let last = 0;
      const step = (now) => {
        if (!this.map || !this.map.getLayer('pulse')) return;
        if (now - last > 50) {
          last = now;
          const p = ((now - t0) % 1800) / 1800;
          this.map.setPaintProperty('pulse', 'circle-radius', 8 + p * 18);
          this.map.setPaintProperty('pulse', 'circle-stroke-opacity', 0.85 * (1 - p));
        }
        this.pulseFrame = requestAnimationFrame(step);
      };
      this.pulseFrame = requestAnimationFrame(step);
    },
    zoomCluster(e) {
      const feature = this.map.queryRenderedFeatures(e.point, { layers: ['clusters'] })[0];
      if (!feature) return;
      this.map.getSource('forests').getClusterExpansionZoom(feature.properties.cluster_id, (err, zoom) => {
        if (!err) this.map.easeTo({ center: feature.geometry.coordinates, zoom: zoom + 0.3, duration: 900 });
      });
    },
    showPopup(e) {
      const f = e.features && e.features[0];
      if (!f) return;
      const p = f.properties;
      const img = imageUrl(p.image);
      const html = `
        <div class="fmap-pop">
          ${img ? `<div class="fp-img" style="background-image:url('${esc(img)}')"></div>` : ''}
          <div class="fp-name">${esc(p.name)}</div>
          <div class="fp-country">${esc(p.country || '')}</div>
          <div class="fp-grid">
            <span><b>${fmt(p.co2, 1)}</b> t CO2</span>
            <span><b>${fmt(p.trees)}</b> trees</span>
            <span><b>${fmt(p.ha, 2)}</b> ha</span>
            <span><b>${fmt(p.available)}</b> ZCO2</span>
          </div>
          <a class="fp-link" href="/areas/${Number(p.id)}" data-area="${Number(p.id)}">Open forest <i class="fas fa-arrow-right"></i></a>
        </div>`;
      new mapboxgl.Popup({ offset: 12, closeButton: true, maxWidth: '240px', className: 'fmap-popup' })
        .setLngLat(f.geometry.coordinates).setHTML(html).addTo(this.map);
    }
  }
};
</script>

<style scoped lang="scss">
.fmap {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  margin: 6px 12px 12px;
  border-radius: 16px;
  overflow: hidden;
  background: #050b08;
}

.fmap-canvas {
  position: absolute;
  inset: 0;
}

.fmap-tools {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  display: flex;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: rgba(8, 18, 12, 0.6);
  backdrop-filter: blur(8px);

  button {
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.74rem;
    font-weight: 700;
    padding: 5px 10px;
    border-radius: 9px;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;

    &.on { background: rgba(61, 220, 151, 0.9); color: #08130b; }
    &:hover:not(.on) { color: #fff; }
  }
}

.fmap-legend {
  position: absolute;
  left: 10px;
  bottom: 10px;
  z-index: 2;
  padding: 8px 12px;
  border-radius: 12px;
  color: #fff;
  font-size: 0.7rem;
  background: rgba(8, 18, 12, 0.62);
  backdrop-filter: blur(8px);
}

.fl-title { font-weight: 800; font-size: 0.8rem; margin-bottom: 4px; }

.fl-scale {
  display: flex;
  align-items: center;
  gap: 6px;
  i { width: 70px; height: 6px; border-radius: 4px; background: linear-gradient(90deg, #9ed77f, #3ddc97, #2fd0c4, #f4c25b); }
}

.fl-pulse { margin-top: 4px; display: flex; align-items: center; gap: 6px; }
.fl-dot { width: 9px; height: 9px; border-radius: 50%; border: 2px solid #3ddc97; animation: fl-ping 1.8s infinite; }
@keyframes fl-ping { 0% { box-shadow: 0 0 0 0 rgba(61, 220, 151, 0.6); } 100% { box-shadow: 0 0 0 8px rgba(61, 220, 151, 0); } }

.fmap-loading {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  pointer-events: none;
  i { font-size: 1.8rem; color: #3ddc97; }
}
</style>

<style lang="scss">
/* popup (rendered by Mapbox outside the component) */
.fmap-popup .mapboxgl-popup-content {
  padding: 0;
  border-radius: 14px;
  overflow: hidden;
  background: var(--card-bg);
  color: var(--text-main);
  box-shadow: var(--shadow-lg);
}
.fmap-popup .mapboxgl-popup-tip { border-top-color: var(--card-bg); border-bottom-color: var(--card-bg); }
.fmap-popup .mapboxgl-popup-close-button { color: #fff; font-size: 18px; right: 4px; top: 2px; text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6); }
.fmap-pop .fp-img { height: 92px; background-size: cover; background-position: center; }
.fmap-pop .fp-name { font-weight: 800; padding: 10px 12px 0; color: var(--text-heading); }
.fmap-pop .fp-country { font-size: 0.75rem; color: var(--text-muted); padding: 0 12px; }
.fmap-pop .fp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 10px; padding: 8px 12px 12px; font-size: 0.75rem; color: var(--text-muted); }
.fmap-pop .fp-grid b { color: var(--text-heading); }
.fmap-pop .fp-link { display: block; margin: 0 12px 12px; padding: 7px 10px; border-radius: 10px; text-align: center; font-size: 0.78rem; font-weight: 700; color: #fff; background: var(--accent-gradient); text-decoration: none; }
.fmap-pop .fp-link:hover { color: #fff; filter: brightness(1.08); }
</style>
